import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, RouteComponentProps } from 'react-router-dom'

import errorLogging from 'services/error-logging'
import App from 'components/App'
import * as userActions from 'store/user/actions'
import * as menuActions from 'store/menus/actions'
import * as pageActions from 'store/pages/actions'
import {
  getComponents,
  getTitle,
  getSocialMetaData,
  getCommentTopics,
  getSidebarComponents,
  getPath,
} from 'store/pages/selectors'
import { isPreRendering } from 'ui-component-library/utils/helpers'
import { getIsRegisteredForOnRamp } from 'store/user/selectors'
import { getResumeCourseBannerStrings } from 'store/strings/selectors'

function AppContainer({ location }: RouteComponentProps) {
  const dispatch = useDispatch()
  const history = useHistory()
  const title = useSelector(getTitle)
  const components = useSelector(getComponents)
  const sidebarComponents = useSelector(getSidebarComponents)
  const commentTopics = useSelector(getCommentTopics)
  const isRegisteredForOnRamp = useSelector(getIsRegisteredForOnRamp)
  const resumeCourseBannerStrings = useSelector(getResumeCourseBannerStrings)
  const socialMetaData = useSelector(getSocialMetaData)
  const pathFetched = useSelector(getPath)
  const [isLoadingBase, setIsLoadingBase] = useState(true)
  const [isLoadingPage, setIsLoadingPage] = useState(true)

  async function fetchBase() {
    setIsLoadingBase(true)
    if (!isPreRendering()) {
      dispatch(userActions.fetchUser())
    }
    await dispatch(menuActions.fetchMenus())
    setIsLoadingBase(false)
  }

  async function fetchPage() {
    if (pathFetched === location.pathname) {
      setIsLoadingPage(false)
      return
    }

    setIsLoadingPage(true)
    try {
      await dispatch(pageActions.fetchPage(location.pathname))
      setIsLoadingPage(false)
    } catch (e) {
      if (isPreRendering()) {
        return
      }

      if (
        e.response &&
        e.response.status === 302 &&
        window.location.host.includes('crossfit')
      ) {
        const { data } = e.response
        const { redirect } = data

        if (redirect === location.pathname) {
          // If we just were told to redirect to the same page we
          // asked for then we want to reload the page. This is to
          // handle urls that are on the legacy site.
          window.location.reload()
        } else {
          window.location.replace(redirect)
        }
      } else if (e.response) {
        history.replace('/404')
      } else {
        errorLogging(e)
      }
    }
  }

  useEffect(() => {
    fetchPage()
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    fetchBase()
  }, [])

  return (
    <App
      title={title}
      components={components}
      sidebarComponents={sidebarComponents}
      isLoadingBase={isLoadingBase}
      isLoadingPage={isLoadingPage}
      isRegisteredForOnRamp={isRegisteredForOnRamp}
      isOnOrderSuccessPages={location.pathname.startsWith('/order-success')}
      resumeCourseBannerStrings={resumeCourseBannerStrings}
      socialMetaData={socialMetaData}
      commentTopics={commentTopics}
    />
  )
}

export default AppContainer
